import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { Button } from "@/components/ui/button";
import { ArrowLeft, PlusCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PriceCategory, PriceType } from "@/types/priceCategory";
import { PriceCategoriesTable } from "@/components/admin/price-categories/PriceCategoriesTable";
import { EditPriceCategoryDialog } from "@/components/admin/price-categories/EditPriceCategoryDialog";
import { NewPriceCategoryDialog } from "@/components/admin/price-categories/NewPriceCategoryDialog";

export default function PriceCategories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<PriceCategory[]>([]);
  const [editingCategory, setEditingCategory] = useState<PriceCategory | null>(null);
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";
    if (!isAuthenticated) {
      navigate("/auth");
    }
    fetchCategories();
  }, [navigate]);

  const fetchCategories = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('cat_price_groups').select('*').order('name');
      if (error) throw error;
      const convertedData = (data || []).map(category => ({
        ...category,
        price_type: (category.price_type || 'fixed') as PriceType,
        fixed_price: category.price_type === 'fixed' ? category.fixed_price : null,
        min_price: category.price_type === 'range' ? category.min_price : undefined,
        max_price: category.price_type === 'range' ? category.max_price : undefined
      }));
      setCategories(convertedData);
    } catch (error) {
      console.error('Erro ao carregar grupos de preços:', error);
      toast.error('Erro ao carregar grupos de preços');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!editingCategory) return;
    try {
      const updateData = {
        name: editingCategory.name,
        prefix: editingCategory.prefix,
        price_type: editingCategory.price_type,
        fixed_price: editingCategory.price_type === 'fixed' ? editingCategory.fixed_price : null,
        min_price: editingCategory.price_type === 'range' ? editingCategory.min_price : null,
        max_price: editingCategory.price_type === 'range' ? editingCategory.max_price : null
      };
      const {
        error
      } = await supabase.from('cat_price_groups').update(updateData).eq('id', editingCategory.id);
      if (error) throw error;
      toast.success('Grupo de preços atualizado com sucesso');
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Erro ao atualizar grupo de preços:', error);
      toast.error('Erro ao atualizar grupo de preços');
    }
  };

  const handleCreate = async (newCategory: Omit<PriceCategory, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const {
        error
      } = await supabase.from('cat_price_groups').insert([{
        name: newCategory.name,
        prefix: newCategory.prefix,
        price_type: newCategory.price_type,
        fixed_price: newCategory.price_type === 'fixed' ? newCategory.fixed_price : null,
        min_price: newCategory.price_type === 'range' ? newCategory.min_price : null,
        max_price: newCategory.price_type === 'range' ? newCategory.max_price : null,
        active: true
      }]);
      if (error) throw error;
      toast.success('Grupo de preços criado com sucesso');
      setIsNewDialogOpen(false);
      fetchCategories();
    } catch (error) {
      console.error('Erro ao criar grupo de preços:', error);
      toast.error('Erro ao criar grupo de preços');
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      
      <div className="flex-1 ml-[250px]">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold">Grupos de Preços</h1>
            <div className="flex gap-2">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/')} 
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </Button>
              <Button 
                onClick={() => setIsNewDialogOpen(true)} 
                className="flex items-center gap-2 bg-blue-700 hover:bg-blue-600"
              >
                <PlusCircle className="w-4 h-4" />
                Novo Grupo
              </Button>
            </div>
          </div>

          <div className="max-w-[1200px]">
            <PriceCategoriesTable 
              categories={categories} 
              onEdit={setEditingCategory} 
            />
          </div>
        </div>

        <EditPriceCategoryDialog 
          category={editingCategory} 
          onClose={() => setEditingCategory(null)} 
          onSave={handleEdit} 
          onChange={setEditingCategory} 
        />

        <NewPriceCategoryDialog 
          open={isNewDialogOpen} 
          onClose={() => setIsNewDialogOpen(false)} 
          onSave={handleCreate} 
        />
      </div>
    </div>
  );
}
